// src/components/dictionary/DictionaryList.jsx

import React, { useState } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import { NAHWU_DICTIONARY, getCategories } from '../../constants/dictionary';
import DictionaryItem from './DictionaryItem';

export default function DictionaryList() {
  const [search, setSearch]                   = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel]     = useState('all');
  const [showFilters, setShowFilters]         = useState(false);

  const categories = getCategories();
  const levels     = ['Dasar', 'Menengah', 'Lanjutan'];

  // ── Filter Logic ───────────────────────────────────────────
  const filteredDictionary = NAHWU_DICTIONARY.filter(item => {
    const q = (search || '').toLowerCase().trim();

    if (q) {
      const hit =
        (item.title       || '').toLowerCase().includes(q) ||
        (item.arabic      || '').includes(search)          ||
        (item.latin       || '').toLowerCase().includes(q) ||
        (item.description || '').toLowerCase().includes(q) ||
        (item.tags        || []).some(t => (t || '').toLowerCase().includes(q));
      if (!hit) return false;
    }

    if (selectedCategory !== 'all' && item.category !== selectedCategory) return false;
    if (selectedLevel    !== 'all' && item.level    !== selectedLevel)    return false;

    return true;
  });

  const hasActiveFilter = search || selectedCategory !== 'all' || selectedLevel !== 'all';

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategory('all');
    setSelectedLevel('all');
  };

  return (
    <div className="space-y-4 px-4 pb-4">

      {/* ── Search Bar ───────────────────────────────── */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 p-3">
        {/* Input Pencarian */}
        <div className="relative">
          <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Cari istilah... (Fa'il, فاعل, pelaku)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="
              w-full pl-10 pr-10 py-3
              bg-stone-50 dark:bg-slate-700
              border border-stone-200 dark:border-slate-600
              rounded-2xl
              text-sm text-slate-800 dark:text-slate-100
              placeholder:text-stone-400
              focus:outline-none focus:border-green-400
              focus:ring-2 focus:ring-green-400/20
              transition-all
            "
          />
          {/* Tombol Hapus Teks */}
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tombol Toggle Filter */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`
            w-full mt-2.5 flex items-center justify-center gap-2
            py-2 rounded-2xl text-xs font-bold
            transition-all active:scale-95
            ${showFilters
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-stone-100 text-stone-600 dark:bg-slate-700 dark:text-slate-400'
            }
          `}
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          {showFilters ? 'Sembunyikan Filter' : 'Tampilkan Filter'}
        </button>

        {/* Panel Filter (Collapsible) */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-stone-100 dark:border-slate-700 space-y-3 animate-fade-in">
            {/* Filter Kategori */}
            <div>
              <label className="block text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                Kategori
              </label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`
                    text-[11px] font-bold px-3 py-1.5 rounded-full transition-all
                    ${selectedCategory === 'all'
                      ? 'bg-green-500 text-white shadow-sm'
                      : 'bg-stone-100 text-stone-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-stone-200'
                    }
                  `}
                >
                  Semua
                </button>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`
                      text-[11px] font-bold px-3 py-1.5 rounded-full transition-all
                      ${selectedCategory === cat
                        ? 'bg-green-500 text-white shadow-sm'
                        : 'bg-stone-100 text-stone-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-stone-200'
                      }
                    `}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter Tingkat */}
            <div>
              <label className="block text-[10px] font-extrabold text-stone-500 dark:text-slate-400 uppercase tracking-widest mb-1.5">
                Tingkat
              </label>
              <div className="flex gap-1.5">
                <button
                  onClick={() => setSelectedLevel('all')}
                  className={`
                    text-[11px] font-bold px-3 py-1.5 rounded-full flex-1 transition-all
                    ${selectedLevel === 'all'
                      ? 'bg-green-500 text-white shadow-sm'
                      : 'bg-stone-100 text-stone-600 dark:bg-slate-700 dark:text-slate-400'
                    }
                  `}
                >
                  Semua
                </button>
                {levels.map(lvl => (
                  <button
                    key={lvl}
                    onClick={() => setSelectedLevel(lvl)}
                    className={`
                      text-[11px] font-bold px-3 py-1.5 rounded-full flex-1 transition-all
                      ${selectedLevel === lvl
                        ? 'bg-green-500 text-white shadow-sm'
                        : 'bg-stone-100 text-stone-600 dark:bg-slate-700 dark:text-slate-400'
                      }
                    `}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Info Filter Aktif ────────────────────────── */}
      {hasActiveFilter && (
        <div className="flex items-center justify-between bg-sky-50 dark:bg-sky-900/20 border border-sky-100 dark:border-sky-800/40 rounded-2xl px-4 py-3 animate-fade-in">
          <p className="text-xs font-semibold text-sky-800 dark:text-sky-300">
            <span className="font-extrabold text-sky-700 dark:text-sky-200">
              {filteredDictionary.length}
            </span>{' '}
            istilah ditemukan
          </p>
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 text-[11px] font-extrabold text-sky-700 dark:text-sky-400 hover:text-sky-900 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Reset
          </button>
        </div>
      )}

      {/* ── Daftar Item Kamus ────────────────────────── */}
      <div className="space-y-3">
        {filteredDictionary.length > 0 ? (
          filteredDictionary.map((item, index) => (
            <DictionaryItem key={item.id} item={item} index={index} />
          ))
        ) : (
          /* Empty State */
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-soft border border-stone-100 dark:border-slate-700 p-8 text-center animate-fade-in">
            <div className="w-16 h-16 bg-stone-100 dark:bg-slate-700 rounded-3xl flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="text-base font-extrabold text-slate-700 dark:text-slate-200 mb-1">
              Tidak Ada Hasil
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4 leading-relaxed">
              Tidak ditemukan istilah yang sesuai dengan pencarianmu
            </p>
            <button
              onClick={handleClearFilters}
              className="
                inline-flex items-center gap-2
                bg-green-500 hover:bg-green-600
                text-white text-xs font-bold
                px-5 py-2.5 rounded-2xl
                transition-all active:scale-95 shadow-soft
              "
            >
              <X className="w-3.5 h-3.5" />
              Reset Pencarian
            </button>
          </div>
        )}
      </div>

      {/* ── Footer Tips ──────────────────────────────── */}
      {filteredDictionary.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-4 border border-stone-100 dark:border-slate-700 shadow-soft text-center">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            <span className="font-bold text-slate-600 dark:text-slate-300">Tips:</span>{' '}
            Kamu bisa mencari dalam bahasa Indonesia, Arab, atau Latin.
          </p>
        </div>
      )}

    </div>
  );
}